import { DateRangeValue } from '../date-range-filter/DateRangeFilter';
import { TableFilter } from '../metadata/propertyConfig';
import { TRISTATE_NO, TRISTATE_YES } from '../more-filters/metadata/FilterOption';

export type SelectOption = { value: string; label: string };

/**
 * Parse a query string into a key-value map (last-value-wins for dupes).
 */
export const parseQueryString = (
  queryString: string
): Record<string, string> => {
  const params: Record<string, string> = {};
  (queryString ?? '').split('&').forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key) {
      params[key] = decodeURIComponent((value || '').replace(/\+/g, ' '));
    }
  });
  return params;
};

/**
 * Parse a query string, collecting repeated keys into arrays.
 */
export const parseQueryStringMulti = (
  queryString: string
): Record<string, string[]> => {
  const params: Record<string, string[]> = {};
  (queryString ?? '').split('&').forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key) {
      const decoded = decodeURIComponent((value || '').replace(/\+/g, ' '));
      if (params[key]) {
        params[key].push(decoded);
      } else {
        params[key] = [decoded];
      }
    }
  });
  return params;
};

/**
 * Convert a tri-state ('either' | 'yes' | 'no') photo / tour radio value
 * to the API boolean string. 'either' (and any other input) omits the param.
 */
export const getPhotosFilterValue = (
  filterValue: string | undefined
): string => {
  if (filterValue === TRISTATE_YES) return 'true';
  if (filterValue === TRISTATE_NO) return 'false';
  return '';
};

/**
 * Build property advanced filter params.
 */
export const createPropertyParams = (opts: {
  occupancyStatus?: string;
  leaseStatus?: string;
  portfolio?: string;
  photosFilter?: string;
  tourFilter?: string;
  lastOrderDate?: string;
}): Record<string, string> => {
  const params: Record<string, string> = {};
  const add = (key: string, val: string | undefined) => {
    if (val !== undefined && val !== '') params[key] = val;
  };
  add('propertyStatus', opts.occupancyStatus);
  add('leaseStatus', opts.leaseStatus);
  add('portfolioID', opts.portfolio);
  add('hasPhotos', getPhotosFilterValue(opts.photosFilter));
  add('has3DTour', getPhotosFilterValue(opts.tourFilter));
  add('lastOrderDateRange', opts.lastOrderDate);
  return params;
};

/**
 * Build order advanced filter params.
 */
export const createOrderParams = (opts: {
  address?: string;
  unableToComplete?: string;
  accessType?: string;
  appointmentDateRange?: DateRangeValue | null;
  orderCompletedDateRange?: DateRangeValue | null;
  services?: SelectOption[];
  orderedBy?: SelectOption[];
}): Record<string, string | boolean | string[]> => {
  const params: Record<string, string | boolean | string[]> = {};

  const add = (key: string, val: string | undefined | boolean) => {
    if (val !== undefined && val !== '') {
      if (key === 'unableToComplete') {
        params[key] = val === 'Yes';
      } else {
        params[key] = val as string;
      }
    }
  };

  add('address', opts.address);
  add('unableToComplete', opts.unableToComplete);
  add('accessType', opts.accessType);
  add('appointmentDateFrom', opts.appointmentDateRange?.from || undefined);
  add('appointmentDateTo', opts.appointmentDateRange?.to || undefined);
  add('completedDateFrom', opts.orderCompletedDateRange?.from || undefined);
  add('completedDateTo', opts.orderCompletedDateRange?.to || undefined);

  if (opts.services && opts.services.length > 0) {
    params.services = opts.services.map((s) => s.value);
  }
  if (opts.orderedBy && opts.orderedBy.length > 0) {
    params.orderedBy = opts.orderedBy.map((o) => o.value);
  }

  return params;
};

/**
 * Count the meaningful number of filters from order params.
 * Date-range pairs (from+to) count as a single filter.
 */
export const countOrderFilters = (
  params: Record<string, string | boolean | string[]>
): number => {
  let count = Object.keys(params).length;
  if (params.appointmentDateFrom && params.appointmentDateTo) count--;
  if (params.completedDateFrom && params.completedDateTo) count--;
  return count;
};

/**
 * Check whether a home-filter object has any non-default value set.
 * Address (the search input) is always excluded; on the property page
 * `activeStatus === 'Active Units'` is also the default and is not
 * counted as an active filter.
 */
export const hasActiveHomeFilters = (
  obj: TableFilter | undefined,
  isProperty: boolean
): boolean => {
  if (!obj) return false;
  const { address: _a, fullAddress: _f, activeStatus, ...rest } = obj;
  const restHasValue = Object.values(rest).some(
    (v) => v !== null && !(Array.isArray(v) && v.length === 0)
  );
  if (restHasValue) return true;
  if (isProperty) return activeStatus != null && activeStatus !== 'Active Units';
  return activeStatus != null;
};

/**
 * Restore a DateRangeValue from parsed query string from/to values.
 */
export const restoreDateRange = (
  from: string | undefined,
  to: string | undefined
): DateRangeValue | null => {
  if (from || to) {
    return {
      preset: 'custom',
      from: from || null,
      to: to || null,
      displayLabel: `${from || ''} \u2013 ${to || ''}`
    };
  }
  return null;
};

/**
 * Restore order filter state from a query string.
 * Returns the resolved setter calls via a structured result object.
 */
export interface OrderFilterRestoreResult {
  simple: Record<string, string>;
  unableToComplete: string | null;
  serviceSlugs: string[];
  orderedByValues: SelectOption[];
  appointmentDateRange: DateRangeValue | null;
  orderCompletedDateRange: DateRangeValue | null;
}

/**
 * Restore the user-settable property advanced-filter fields from a query
 * string. Values are the serialized forms (`hasPhotos`/`has3DTour` are
 * 'true'|'false'; `lastOrderDateRange` is the option value); null when absent.
 */
export interface PropertyFilterRestoreResult {
  hasPhotos: string | null;
  has3DTour: string | null;
  lastOrderDateRange: string | null;
}

export const restorePropertyFiltersFromQS = (
  qs: string
): PropertyFilterRestoreResult => {
  const values = parseQueryString(qs);
  return {
    hasPhotos: values.hasPhotos ?? null,
    has3DTour: values.has3DTour ?? null,
    lastOrderDateRange: values.lastOrderDateRange ?? null
  };
};

/**
 * Count active property filters for the More Filters badge: serialized
 * moreFilters keys plus a non-default "Properties Displayed" (anything other
 * than Active Units — Inactive or All — counts as one).
 */
export const countPropertyMoreFilters = (
  moreFilters: string,
  activeStatusValue: { value: string } | null | undefined
): number =>
  Object.keys(parseQueryString(moreFilters)).length +
  (activeStatusValue?.value === 'Active Units' ? 0 : 1);

export const restoreOrderFiltersFromQS = (
  qs: string
): OrderFilterRestoreResult => {
  const values = parseQueryString(qs);
  const multi = parseQueryStringMulti(qs);

  const simpleKeys = ['address', 'accessType'];
  const simple: Record<string, string> = {};
  simpleKeys.forEach((key) => {
    if (values[key]) simple[key] = values[key];
  });

  let utc: string | null = null;
  if (values.unableToComplete) {
    utc = values.unableToComplete === 'true' ? 'Yes' : 'No';
  }

  const slugs = multi.services || [];
  const obVals = (multi.orderedBy || []).map((v) => ({
    value: v,
    label: v
  }));

  return {
    simple,
    unableToComplete: utc,
    serviceSlugs: slugs,
    orderedByValues: obVals,
    appointmentDateRange: restoreDateRange(
      values.appointmentDateFrom,
      values.appointmentDateTo
    ),
    orderCompletedDateRange: restoreDateRange(
      values.completedDateFrom,
      values.completedDateTo
    )
  };
};
